import React, { Fragment, useState } from "react";
import { toast } from "react-toastify";
import NumberFormat from "react-number-format";
import ReactModal from "react-modal";

import RowActions from "./row-actions";
import { LoadingButton } from "../shared/loading-button";
import {
  formatAppliesTo,
  formatBracketAmount,
  formatWholeDollars,
} from "../../helpers/commissionFormat";

const NEW_ROW_ID = "new";
const MIN_BRACKETS = 2;

const toNumber = (value) => (value === "" || value === null || value === undefined ? null : Number(value));

const DollarInput = ({ name, value, onChange }) => (
  <NumberFormat
    name={name}
    value={value}
    thousandSeparator={true}
    prefix={"$"}
    decimalScale={0}
    allowNegative={false}
    className="commissions-input"
    onValueChange={({ value: nextValue }) => onChange(nextValue)}
  />
);

const ServiceFeesTable = ({
  brackets,
  onUpdate,
  onCreate,
  onDelete,
  editingKey,
  setEditingKey,
  isSaving,
}) => {
  const [draft, setDraft] = useState(null);
  const [bracketToDelete, setBracketToDelete] = useState(null);

  const keyFor = (id) => `fee:${id}`;
  const isAdding = editingKey === keyFor(NEW_ROW_ID);
  const openRow = brackets[brackets.length - 1];

  const rows = isAdding
    ? [
        ...brackets.slice(0, -1),
        { id: NEW_ROW_ID, lowBracket: openRow.lowBracket, highBracket: null, serviceFee: null },
        openRow,
      ]
    : brackets;

  const handleEdit = (row) => {
    setDraft({
      lowBracket: String(row.lowBracket),
      highBracket: row.highBracket === null ? "" : String(row.highBracket),
      serviceFee: String(row.serviceFee),
    });
    setEditingKey(keyFor(row.id));
  };

  const handleAdd = () => {
    setDraft({ lowBracket: String(openRow.lowBracket), highBracket: "", serviceFee: "" });
    setEditingKey(keyFor(NEW_ROW_ID));
  };

  const handleCancel = () => {
    setDraft(null);
    setEditingKey(null);
  };

  const validate = (row, index) => {
    const isNew = row.id === NEW_ROW_ID;
    const isLast = index === rows.length - 1;
    const lowBracket = index === 0 && !isNew ? toNumber(draft.lowBracket) : row.lowBracket;
    const highBracket = isLast ? null : toNumber(draft.highBracket);
    const serviceFee = toNumber(draft.serviceFee);
    const next = rows[index + 1];

    if (lowBracket === null) {
      return { error: "Low bracket is required." };
    }

    if (!isLast && highBracket === null) {
      return { error: "High bracket is required." };
    }

    if (serviceFee === null) {
      return { error: "Service fee is required." };
    }

    if (!isLast && highBracket <= lowBracket) {
      return { error: "High bracket must be greater than the low bracket." };
    }

    if (!isLast && !isNew && next && next.highBracket !== null && highBracket >= next.highBracket) {
      return { error: "High bracket must be lower than the next row's high bracket." };
    }

    return { lowBracket, highBracket, serviceFee };
  };

  const handleConfirm = async (row, index) => {
    const result = validate(row, index);

    if (result.error) {
      toast.error(result.error);
      return;
    }

    const isSaved =
      row.id === NEW_ROW_ID
        ? await onCreate({ highBracket: result.highBracket, serviceFee: result.serviceFee })
        : await onUpdate(row.id, {
            ...(index === 0 ? { lowBracket: result.lowBracket } : {}),
            ...(result.highBracket !== null ? { highBracket: result.highBracket } : {}),
            serviceFee: result.serviceFee,
          });

    if (isSaved) {
      handleCancel();
    }
  };

  const handleDeleteConfirm = async () => {
    if (await onDelete(bracketToDelete.id)) {
      setBracketToDelete(null);
    }
  };

  const setDraftField = (field) => (value) => setDraft((current) => ({ ...current, [field]: value }));

  return (
    <Fragment>
      <div className="commissions-table" data-testid="commission-service-fees-table">
        <div className="commissions-table-header">
          <h3>Service Fees</h3>
          <button
            type="button"
            className="base-button evo-blue"
            onClick={handleAdd}
            disabled={editingKey !== null || isSaving}
          >
            Add bracket
          </button>
        </div>
        <table>
          <thead>
            <tr>
              <th>Low bracket</th>
              <th>High bracket</th>
              <th>Applies to</th>
              <th>Service fee</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const isEditing = editingKey === keyFor(row.id);
              const isNew = row.id === NEW_ROW_ID;
              const isLast = index === rows.length - 1;
              const lowBracket =
                isEditing && index === 0 && !isNew ? toNumber(draft.lowBracket) : row.lowBracket;
              const highBracket = isEditing && !isLast ? toNumber(draft.highBracket) : row.highBracket;

              return (
                <tr key={row.id} className={isEditing ? "commissions-row-editing" : undefined}>
                  <td>
                    {isEditing && index === 0 && !isNew ? (
                      <DollarInput
                        name="lowBracket"
                        value={draft.lowBracket}
                        onChange={setDraftField("lowBracket")}
                      />
                    ) : (
                      formatBracketAmount(row.lowBracket)
                    )}
                  </td>
                  <td>
                    {isLast ? (
                      "-"
                    ) : isEditing ? (
                      <DollarInput
                        name="highBracket"
                        value={draft.highBracket}
                        onChange={setDraftField("highBracket")}
                      />
                    ) : (
                      formatBracketAmount(row.highBracket)
                    )}
                  </td>
                  <td>
                    {lowBracket !== null && (isLast || (highBracket !== null && highBracket > lowBracket))
                      ? formatAppliesTo(lowBracket, isLast ? null : highBracket)
                      : "-"}
                  </td>
                  <td>
                    {isEditing ? (
                      <DollarInput
                        name="serviceFee"
                        value={draft.serviceFee}
                        onChange={setDraftField("serviceFee")}
                      />
                    ) : (
                      formatWholeDollars(row.serviceFee)
                    )}
                  </td>
                  <td className="commissions-actions">
                    <RowActions
                      isEditing={isEditing}
                      isEditDisabled={editingKey !== null}
                      isSaving={isSaving}
                      onEdit={() => handleEdit(row)}
                      onConfirm={() => handleConfirm(row, index)}
                      onCancel={handleCancel}
                      onDelete={() => setBracketToDelete(row)}
                      showDelete={!isLast && brackets.length > MIN_BRACKETS}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ReactModal
        isOpen={!!bracketToDelete}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => setBracketToDelete(null)}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "auto",
            padding: "40px",
            border: "none",
            maxWidth: "700px",
            maxHeight: "50%",
            borderRadius: "0",
            boxShadow: "10px 10px 91px -17px rgb(0 0 0 / 25%)",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <div className="modal-header base-modal">
          <h4>Delete Service Fee Bracket</h4>
        </div>

        <div className="modal-body base-modal modal-body-with-scroll">
          <div className="w-100 mt-20">
            {bracketToDelete &&
              `Delete the ${formatAppliesTo(
                bracketToDelete.lowBracket,
                bracketToDelete.highBracket
              )} bracket? The next bracket will extend to cover this range.`}
          </div>
        </div>

        <div className="modal-footer w-100 mt-10 d-flex gap-10">
          <div className="w-100 mt-20 d-flex gap-20">
            <button
              className="base-button evo-blue flex-half"
              type="button"
              onClick={() => setBracketToDelete(null)}
              disabled={isSaving}
            >
              Cancel
            </button>

            <LoadingButton
              className="base-button btn-danger flex-half"
              type="button"
              onClick={handleDeleteConfirm}
              isLoading={isSaving}
            >
              Delete Bracket
            </LoadingButton>
          </div>
        </div>
      </ReactModal>
    </Fragment>
  );
};

export default ServiceFeesTable;
